import { useMemo } from 'react';
import type { HistoryRange } from '@wave/shared';
import { EChart, type EChartsOption } from '../EChart';
import { useIsNarrow } from '../../lib/useMediaQuery';
import { useBalanceHistoryByCcy } from './useBalanceHistory';
import { CHART_COLORS, EmptyHistory, chartGrid, chartLegend, tooltipFit, valueAxisLabel } from './ChartsPanel';
import { fmtTs } from './TrendChart';

const CCY_PALETTE = ['#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777'];

/** Суммарный остаток по каждой валюте в родной валюте. Легенда переключает линии. */
export function CcyChart({ range }: { range: HistoryRange }) {
  const { data, isLoading } = useBalanceHistoryByCcy(range);
  const narrow = useIsNarrow();
  const ccys = data?.ccys ?? [];

  const option = useMemo<EChartsOption>(() => {
    // Ось X — объединение всех ts (серии могут иметь пропуски).
    const allTs = [...new Set(ccys.flatMap((c) => c.points.map((p) => p.ts)))].sort();
    return {
      animation: false,
      grid: chartGrid(narrow),
      tooltip: { trigger: 'axis', ...tooltipFit(narrow) },
      legend: chartLegend(narrow),
      xAxis: { type: 'category', data: allTs, axisLabel: { color: CHART_COLORS.text, formatter: (v: string) => fmtTs(v, range), ...(narrow ? { fontSize: 10, hideOverlap: true } : {}) } },
      yAxis: { type: 'value', axisLabel: valueAxisLabel(narrow), splitLine: { lineStyle: { color: CHART_COLORS.grid } } },
      dataZoom: [{ type: 'inside' }, { type: 'slider', height: 18, bottom: 8 }],
      series: ccys.map((c, i) => {
        const byTs = new Map(c.points.map((p) => [p.ts, Number(p.amount)]));
        return {
          name: c.ccy,
          type: 'line' as const,
          data: allTs.map((ts) => byTs.get(ts) ?? null),
          connectNulls: true,
          showSymbol: false,
          lineStyle: { width: 1.5, color: CCY_PALETTE[i % CCY_PALETTE.length] },
          itemStyle: { color: CCY_PALETTE[i % CCY_PALETTE.length] },
        };
      }),
    };
  }, [ccys, range, narrow]);

  if (isLoading) return <div className="h-40 animate-pulse rounded bg-bg3" />;
  const maxPoints = Math.max(0, ...ccys.map((c) => c.points.length));
  if (maxPoints < 2) return <EmptyHistory pointsCount={maxPoints} />;
  return <EChart option={option} height={340} />;
}
